import { useState, useCallback } from 'react';
import { contentAPI } from '../../services/api';

/**
 * useContentAdmin - Testimonials + Trusted Companies CRUD.
 */
export const useContentAdmin = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [trustedCompanies, setTrustedCompanies] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [newTestimonial, setNewTestimonial] = useState({ name: '', role: '', quote: '', photo: '' });
  const [newCompany, setNewCompany] = useState({ name: '', logo_url: '', order: 0 });

  const fetchContentData = useCallback(async () => {
    setContentLoading(true);
    try {
      const [testimonialsData, companiesData] = await Promise.all([
        contentAPI.getTestimonialsAdmin().catch(() => contentAPI.getTestimonials()),
        contentAPI.getTrustedCompaniesAdmin().catch(() => contentAPI.getTrustedCompanies()),
      ]);
      setTestimonials(testimonialsData || []);
      setTrustedCompanies(companiesData || []);
    } catch (error) {
      console.error('Failed to fetch content:', error);
    } finally {
      setContentLoading(false);
    }
  }, []);

  const handleCreateTestimonial = useCallback(async () => {
    try {
      const result = await contentAPI.createTestimonial(newTestimonial);
      setTestimonials((prev) => [result, ...prev]);
      setShowTestimonialModal(false);
      setNewTestimonial({ name: '', role: '', quote: '', photo: '' });
    } catch (error) {
      console.error('Failed to create testimonial:', error);
      alert('Error creating testimonial');
    }
  }, [newTestimonial]);

  const handleUpdateTestimonial = useCallback(async () => {
    try {
      const result = await contentAPI.updateTestimonial(editingTestimonial.id, editingTestimonial);
      setTestimonials((prev) => prev.map((t) => (t.id === editingTestimonial.id ? result : t)));
      setEditingTestimonial(null);
    } catch (error) {
      console.error('Failed to update testimonial:', error);
      alert('Error updating testimonial');
    }
  }, [editingTestimonial]);

  const handleDeleteTestimonial = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await contentAPI.deleteTestimonial(id);
      setTestimonials((prev) => prev.filter((t) => t.id !== id));
    } catch (error) {
      console.error('Failed to delete testimonial:', error);
    }
  }, []);

  const handleCreateCompany = useCallback(async () => {
    try {
      const result = await contentAPI.createTrustedCompany(newCompany);
      setTrustedCompanies((prev) => [...prev, result]);
      setShowCompanyModal(false);
      setNewCompany({ name: '', logo_url: '', order: 0 });
    } catch (error) {
      console.error('Failed to create company:', error);
      alert('Error creating company');
    }
  }, [newCompany]);

  const handleUpdateCompany = useCallback(async () => {
    try {
      const result = await contentAPI.updateTrustedCompany(editingCompany.id, editingCompany);
      setTrustedCompanies((prev) => prev.map((c) => (c.id === editingCompany.id ? result : c)));
      setEditingCompany(null);
    } catch (error) {
      console.error('Failed to update company:', error);
      alert('Error updating company');
    }
  }, [editingCompany]);

  const handleDeleteCompany = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    try {
      await contentAPI.deleteTrustedCompany(id);
      setTrustedCompanies((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      console.error('Failed to delete company:', error);
    }
  }, []);

  return {
    testimonials,
    trustedCompanies,
    contentLoading,
    showTestimonialModal,
    setShowTestimonialModal,
    showCompanyModal,
    setShowCompanyModal,
    editingTestimonial,
    setEditingTestimonial,
    editingCompany,
    setEditingCompany,
    newTestimonial,
    setNewTestimonial,
    newCompany,
    setNewCompany,
    fetchContentData,
    handleCreateTestimonial,
    handleUpdateTestimonial,
    handleDeleteTestimonial,
    handleCreateCompany,
    handleUpdateCompany,
    handleDeleteCompany,
  };
};
